import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Container, TextField, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ReportSummaryCard, { summaryCardsRowSx } from "../components/Reports/ReportSummaryCard";
import { useNavigation } from "../contexts/RouterContext";
import { filterFieldSx } from "../theme/filterStyles";
import { colors } from "../theme/designSystem";
import { aiUsageService, AiUsageGroup, AiUsageReport } from "../services/aiUsageService";

/**
 * Consumo de IA (harness H0 §4.4, Tecnologia > Consumo de IA): quanto a clínica gastou com IA no período, por
 * origem, agente, modelo e dia, e onde está em relação ao orçamento do mês. Em US$, a moeda do provedor (D-HAR-9).
 */

const usd = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 4 });
const inteiro = (v: number) => v.toLocaleString("pt-BR");
const dataBr = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const Bloco: React.FC<{ titulo: string; grupos: AiUsageGroup[]; total: number }> = ({ titulo, grupos, total }) => (
  <Box sx={{ flex: "1 1 280px", minWidth: 0, border: `1px solid ${colors.backgroundAlt}`, borderRadius: "8px", p: 1.5, backgroundColor: colors.white }}>
    <Typography component="h2" sx={{ fontSize: 13, fontWeight: 700, mb: 1, color: colors.textPrimary }}>{titulo}</Typography>
    {grupos.length === 0 ? (
      <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>Sem uso no período.</Typography>
    ) : (
      grupos.map((g) => (
        <Box key={g.key || g.name} sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, fontSize: 12.5, py: 0.5, borderBottom: `1px solid ${colors.backgroundSubtle}`, "&:last-of-type": { borderBottom: "none" } }}>
          <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere", color: colors.textPrimary }}>{g.name}</Box>
          <Box component="span" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", color: colors.textSecondary }}>
            {inteiro(g.calls)} chamadas · <strong style={{ color: colors.textStrong }}>{usd(g.costUsd)}</strong>
            {total > 0 && ` (${Math.round((g.costUsd / total) * 100)}%)`}
          </Box>
        </Box>
      ))
    )}
  </Box>
);

const AiUsage: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const entityId = user?.entityId || "";

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState<AiUsageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => { document.title = "Clinic4Us - Consumo de IA"; }, []);

  const carregar = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setErro(null);
    try {
      setData(await aiUsageService.get(entityId, from, to));
    } catch {
      setErro("Não foi possível carregar o consumo de IA.");
    } finally {
      setLoading(false);
    }
  }, [entityId, from, to]);

  useEffect(() => { carregar(); }, [carregar]);

  const orcamento = data?.monthlyBudgetUsd ?? null;
  const pctOrcamento = orcamento ? Math.round(((data?.monthSpentUsd ?? 0) / orcamento) * 100) : 0;
  const corOrcamento = !orcamento ? undefined : pctOrcamento >= 100 ? colors.errorHover : pctOrcamento >= (data?.budgetAlertPercent ?? 80) ? colors.warningSoftText : colors.successHover;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: colors.background }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Consumo de IA</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Quanto a clínica gastou com inteligência artificial no período, por origem, agente, modelo e dia.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField size="small" label="De" type="date" value={from || data?.from || ""} onChange={(e) => setFrom(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} />
                <TextField size="small" label="Até" type="date" value={to || data?.to || ""} onChange={(e) => setTo(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} />
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={() => { setFrom(""); setTo(""); }} disabled={(!from && !to) || loading} />
              </div>
            </div>

            <Box className="c4u-info-box">
              <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
              <Typography>
                Valores em dólar, a moeda do provedor de IA, calculados pelo preço de cada modelo no momento do uso.
                O Suporte Clinic4Us é custo da plataforma e não entra no gasto da clínica nem no orçamento. Sem período
                escolhido, mostra o mês atual.
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} aria-label="Carregando" />
              </Box>
            ) : erro ? (
              <Typography role="alert" sx={{ textAlign: "center", color: colors.errorHover, py: 4 }}>{erro}</Typography>
            ) : data && (
              <>
                <Box sx={summaryCardsRowSx}>
                  <ReportSummaryCard label="Gasto da clínica no período" value={usd(data.costUsd)}
                    sub={data.unpricedCalls > 0 ? `${data.unpricedCalls} chamadas sem preço cadastrado (fora do total)` : undefined} subColor={colors.warningSoftText} />
                  <ReportSummaryCard label="Chamadas à IA" value={inteiro(data.calls)}
                    sub={data.errors > 0 ? `${inteiro(data.errors)} com erro (também consomem)` : "nenhuma com erro"} />
                  <ReportSummaryCard label="Tokens" value={inteiro(data.inputTokens + data.outputTokens + data.cacheTokens)}
                    sub={`${inteiro(data.inputTokens)} entrada · ${inteiro(data.outputTokens)} saída · ${inteiro(data.cacheTokens)} cache`} />
                  <ReportSummaryCard label="Orçamento deste mês"
                    value={orcamento ? `${pctOrcamento}%` : "Sem orçamento"} color={corOrcamento}
                    sub={orcamento
                      ? `${usd(data.monthSpentUsd)} de ${usd(orcamento)} · ${data.budgetBlocks ? "bloqueia ao atingir" : "só avisa"}`
                      : "Defina em Tecnologia > Configuração de IA"} />
                </Box>

                {data.platformCostUsd > 0 && (
                  <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, mb: 1.5 }}>
                    Suporte Clinic4Us no período: {usd(data.platformCostUsd)} (custo da plataforma, não da clínica).
                  </Typography>
                )}

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
                  <Bloco titulo="Por origem" grupos={data.bySource} total={data.costUsd + data.platformCostUsd} />
                  <Bloco titulo="Por agente" grupos={data.byAgent} total={data.costUsd + data.platformCostUsd} />
                  <Bloco titulo="Por modelo" grupos={data.byModel} total={data.costUsd + data.platformCostUsd} />
                </Box>

                <Typography component="h2" sx={{ fontSize: 13, fontWeight: 700, mb: 1, color: colors.textPrimary }}>Por dia</Typography>
                <Box className="c4u-table">
                  <Box className="c4u-table__header">
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px" }}>Dia</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px", textAlign: "right" }}>Chamadas</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "1 1 120px", textAlign: "right" }}>Custo</Box>
                  </Box>
                  {data.byDay.length === 0 ? (
                    <Typography sx={{ px: 2, py: 3, fontSize: 13, color: colors.textSecondary, textAlign: "center" }}>
                      Nenhum uso de IA no período escolhido.
                    </Typography>
                  ) : (
                    data.byDay.map((d) => (
                      <Box key={d.day} className="c4u-table__row">
                        <Box className="admin-plans-cell" data-label="Dia" sx={{ flex: "0 0 110px", fontWeight: 600 }}>{dataBr(d.day)}</Box>
                        <Box className="admin-plans-cell" data-label="Chamadas" sx={{ flex: "0 0 100px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{inteiro(d.calls)}</Box>
                        <Box className="admin-plans-cell" data-label="Custo" sx={{ flex: "1 1 120px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{usd(d.costUsd)}</Box>
                      </Box>
                    ))
                  )}
                </Box>
              </>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default AiUsage;
